// 상점 「신규」 배지(S7, STAGE-WORLD-PRD 5.2): 새 마을에 처음 들어가면 그 마을 상점에서 새로 열린 탭에 「신규」를 단다.
// 본 탭은 창을 닫을 때 기록한다(보는 동안 배지가 사라지지 않게). 저장은 새 키 'dungeonhero.shopSeen.v1'(게임 저장과 따로, UI 표시 전용).
// 저장이 막힌 환경(웹뷰·차단된 사이트 데이터)에서는 이번 접속 동안만 기억한다 — 배지가 다시 보일 뿐 게임은 그대로다.
import type { NpcRole } from '../shared/rpg';
import { TOWN_GEAR_TOWNS } from '../shared/content/townShop';
import { ALCHEMIST_TOWN, JEWELER_TOWN, POTIONS } from '../shared/content/consumables';

export const SHOP_SEEN_KEY = 'dungeonhero.shopSeen.v1';
/** 배지를 다는 탭: 마을 장비(잡화상·대장간) · 물약(새 등급) · 보석(보석상) · 연금(연금술사) */
export type ShopBadgeTab = 'town' | 'potion' | 'gem' | 'alchemy';
export function seenKey(role: NpcRole, tab: ShopBadgeTab, town: number): string { return role + ':' + tab + '@' + town; }

/** 그 마을에서 새로 열린 (NPC, 탭) 목록. 마을①은 처음 상점이라 배지가 없다 */
export function newShopTabs(town: number): { role: NpcRole; tab: ShopBadgeTab }[] {
  const out: { role: NpcRole; tab: ShopBadgeTab }[] = [];
  // 시작 마을①은 처음부터 다 새것이라 「신규」를 달지 않는다(장비 통합 뒤 마을①도 6부위 장비를 판다 — ECONOMY 14절)
  if (town > 1 && TOWN_GEAR_TOWNS.includes(town)) out.push({ role: 'shop', tab: 'town' }, { role: 'forge', tab: 'town' });
  if (town > 1 && POTIONS.some((p) => p.town === town)) out.push({ role: 'shop', tab: 'potion' });
  if (town === JEWELER_TOWN) out.push({ role: 'jeweler', tab: 'gem' });
  if (town === ALCHEMIST_TOWN) out.push({ role: 'alchemist', tab: 'alchemy' });
  return out;
}

interface StorageLike { getItem(k: string): string | null; setItem(k: string, v: string): void }

/** 저장 문자열 → 본 키 목록(틀리면 빈 목록: 배지가 다시 보일 뿐이다) */
export function parseSeen(raw: string | null): string[] {
  if (!raw) return [];
  try {
    const p = JSON.parse(raw) as { v?: unknown; seen?: unknown };
    if (p.v !== 1 || !Array.isArray(p.seen)) return [];
    return p.seen.filter((s): s is string => typeof s === 'string' && s.length > 0 && s.length <= 40).slice(0, 200);
  } catch { return []; }
}
export function serializeSeen(seen: Iterable<string>): string { return JSON.stringify({ v: 1, seen: [...seen].sort() }); }

export class ShopSeen {
  private readonly seen: Set<string>;
  /** 이번 창에서 본 탭(창을 닫을 때 기록) */
  private readonly viewing = new Set<string>();
  /** 바뀌면 오른다(RpgUi 재그리기 키) */
  version = 0;

  constructor(private readonly store: StorageLike | null = defaultStore()) {
    let raw: string | null = null;
    try { raw = store ? store.getItem(SHOP_SEEN_KEY) : null; } catch { raw = null; }
    this.seen = new Set(parseSeen(raw));
  }

  /** 그 마을 그 NPC 탭에 「신규」 배지가 붙는지 */
  isNew(role: NpcRole, tab: ShopBadgeTab, town: number): boolean {
    return newShopTabs(town).some((t) => t.role === role && t.tab === tab) && !this.seen.has(seenKey(role, tab, town));
  }
  /** 그 NPC 창에 새 탭이 하나라도 있는지 */
  npcHasNew(role: NpcRole, town: number): boolean { return newShopTabs(town).some((t) => t.role === role && this.isNew(role, t.tab, town)); }

  /** 지금 보는 탭(배지는 창을 닫을 때까지 남는다) */
  view(role: NpcRole, tab: ShopBadgeTab, town: number): void {
    if (this.isNew(role, tab, town)) this.viewing.add(seenKey(role, tab, town));
  }
  /** 창을 닫았다: 본 탭을 기록한다 */
  commit(): void {
    if (this.viewing.size === 0) return;
    for (const k of this.viewing) this.seen.add(k);
    this.viewing.clear();
    this.version++;
    try { this.store?.setItem(SHOP_SEEN_KEY, serializeSeen(this.seen)); } catch { /* 저장 실패면 다음 접속에 배지가 다시 보인다 */ }
  }
  /** 테스트·QA: 본 키 목록 */
  keys(): string[] { return [...this.seen].sort(); }
}

function defaultStore(): StorageLike | null {
  try { return typeof localStorage === 'undefined' ? null : localStorage; } catch { return null; }
}
